import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react-lite';
import { EditText } from '../../../../component/ui/edit-text/edit-text';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { AccountTypeEnum } from '../../../../api/types/settings/accounting/chart-of-accounts/chart-of-accounts.type';
import { CardBrandType } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { createChartOfAccountOperation } from '../operations/create-chart-of-account-operation';

interface AddChartOfAccountDialogProps {
  visible: boolean;
  onHide: () => void;
  onCreated: () => void;
}

const accountTypeOptions = [
  { label: 'מזומן', value: AccountTypeEnum.CASH },
  { label: 'שיק', value: AccountTypeEnum.CHECK },
  { label: 'בנק', value: AccountTypeEnum.BANK },
  { label: 'אשראי', value: AccountTypeEnum.CREDIT_CARD },
  { label: 'הכנסות', value: AccountTypeEnum.INCOME },
];

const cardBrandOptions = [
  { label: 'Visa', value: CardBrandType.VISA },
  { label: 'Mastercard', value: CardBrandType.MASTERCARD },
  { label: 'Isracard', value: CardBrandType.ISRACARD },
  { label: 'American Express', value: CardBrandType.AMERICAN_EXPRESS },
  { label: 'Diners Club', value: CardBrandType.DINERS_CLUB },
  { label: 'Discover', value: CardBrandType.DISCOVER },
  { label: 'JCB', value: CardBrandType.JCB },
  { label: 'Maestro', value: CardBrandType.MAESTRO },
  { label: 'Private label', value: CardBrandType.PRIVATE_LABEL },
];

export const AddChartOfAccountDialog: React.FC<AddChartOfAccountDialogProps> =
  observer(({ visible, onHide, onCreated }) => {
    const [accountCode, setAccountCode] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountType, setAccountType] = useState<AccountTypeEnum | null>(
      null,
    );
    const [creditCardCompany, setCreditCardCompany] =
      useState<CardBrandType | null>(null);
    const [bankName, setBankName] = useState('');
    const [branchIds, setBranchIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (visible) {
        setAccountCode('');
        setAccountName('');
        setAccountType(null);
        setCreditCardCompany(null);
        setBankName('');
        setBranchIds([]);
        setLoading(false);
      }
    }, [visible]);

    const handleAccountTypeChange = (next: AccountTypeEnum | null) => {
      setAccountType(next);
      if (next !== AccountTypeEnum.CREDIT_CARD) {
        setCreditCardCompany(null);
      }
      if (next !== AccountTypeEnum.BANK) {
        setBankName('');
      }
    };

    const handleSubmit = async () => {
      if (!accountType) {
        return;
      }
      await createChartOfAccountOperation(
        {
          accountCode: accountCode.trim(),
          accountName: accountName.trim(),
          accountType,
          creditCardCompany:
            accountType === AccountTypeEnum.CREDIT_CARD
              ? creditCardCompany
              : null,
          bankName:
            accountType === AccountTypeEnum.BANK
              ? bankName.trim() || null
              : null,
          branchIds,
        },
        setLoading,
        () => {
          onCreated();
          onHide();
        },
      );
    };

    const branchOptions = globalSettingsBranches.branches.map((b) => ({
      label: b.branchName,
      value: b.id,
    }));

    const footer = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button
          label="ביטול"
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-text"
          disabled={loading}
        />
        <Button
          label="שמירה"
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        />
      </div>
    );

    return (
      <Dialog
        header="הוספת חשבון חדש"
        visible={visible}
        onHide={onHide}
        style={{ width: '560px', maxWidth: '95vw' }}
        footer={footer}
        dismissableMask
        draggable={false}
        resizable={false}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            direction: 'rtl',
            paddingTop: '4px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <EditText
              title="קוד חשבון"
              value={accountCode}
              onChange={setAccountCode}
              maxLength={20}
            />
            <EditText
              title="שם חשבון"
              value={accountName}
              onChange={setAccountName}
              maxLength={100}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
            }}
          >
            <label style={{ fontSize: '14px', color: '#4b5563' }}>
              סוג חשבון
            </label>
            <Dropdown
              value={accountType}
              options={accountTypeOptions}
              onChange={(e) => handleAccountTypeChange(e.value)}
              placeholder="בחר סוג חשבון"
              style={{
                height: '36px',
                marginTop: '4px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </div>

          {accountType === AccountTypeEnum.CREDIT_CARD && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '16px',
              }}
            >
              <label style={{ fontSize: '14px', color: '#4b5563' }}>
                חברת אשראי
              </label>
              <Dropdown
                value={creditCardCompany}
                options={cardBrandOptions}
                onChange={(e) => setCreditCardCompany(e.value)}
                placeholder="בחר חברת אשראי"
                showClear
                style={{
                  height: '36px',
                  marginTop: '4px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </div>
          )}

          {accountType === AccountTypeEnum.BANK && (
            <EditText
              title="שם הבנק"
              value={bankName}
              onChange={setBankName}
              maxLength={50}
            />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
            }}
          >
            <label style={{ fontSize: '14px', color: '#4b5563' }}>שלוחות</label>
            <MultiSelect
              value={branchIds}
              options={branchOptions}
              onChange={(e) => setBranchIds(e.value as number[])}
              placeholder="בחר שלוחות"
              maxSelectedLabels={3}
              selectedItemsLabel={`${branchIds.length} שלוחות נבחרו`}
              style={{
                height: '36px',
                marginTop: '4px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </div>
        </div>
      </Dialog>
    );
  });
